import { useState } from "react";
import { CheckCircle2, Loader2, Phone, MessageSquareText, ImagePlus, X } from "lucide-react";
import { PHONE_DISPLAY, TEL_HREF, SMS_HREF, CLEANING_ADDONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIRBNB_SERVICE = "Short-Term Rental / Airbnb Turnover";

const SERVICES = [
  "Standard Cleaning",
  "Deep Cleaning",
  "Maintenance / Recurring Cleaning",
  "Move-In / Move-Out Cleaning",
  AIRBNB_SERVICE,
  "Commercial Cleaning",
  "Apartment / Rental Turnover",
  "Junk Removal",
  "Property Cleanout",
  "Not Sure — Help Me Choose",
];

const YESNO = ["Yes", "No"];

const ASSESSMENTS = [
  {
    key: "overallCondition",
    title: "Overall Condition",
    options: [
      { value: "Light", desc: "Generally maintained, recently cleaned, minimal buildup" },
      { value: "Moderate", desc: "Noticeable dust, grime, grease or buildup" },
      { value: "Heavy", desc: "Significant buildup, neglected areas or extensive cleaning needed" },
    ],
  },
  {
    key: "kitchenCondition",
    title: "Kitchen Condition",
    options: [
      { value: "Light", desc: "Generally maintained, minimal grease or buildup" },
      { value: "Moderate", desc: "Noticeable grease, crumbs, residue or sticky surfaces" },
      { value: "Heavy", desc: "Heavy grease, food residue or significant buildup" },
    ],
  },
  {
    key: "bathroomCondition",
    title: "Bathroom Condition",
    options: [
      { value: "Light", desc: "Minimal soap scum or water spots" },
      { value: "Moderate", desc: "Visible soap scum, grime or water buildup" },
      { value: "Heavy", desc: "Thick soap scum, hard-water staining or significant grime" },
    ],
  },
  {
    key: "floorsCondition",
    title: "Floors & Dust",
    options: [
      { value: "Light", desc: "Light dust; floors generally maintained" },
      { value: "Moderate", desc: "Visible dust, pet hair, footprints or debris" },
      { value: "Heavy", desc: "Heavy dust/debris, significant pet hair or sticky/soiled floors" },
    ],
  },
];

const ADDITIONAL_CONDITIONS = [
  "Excessive pet hair",
  "Heavy grease/buildup",
  "Excessive clutter",
  "Pest/insect evidence",
  "Smoke/nicotine residue",
  "Significant trash/debris",
  "None of the above",
];

const initial = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  companyName: "",
  preferredContact: "Call",
  service: "",
  propertyUse: "Residential",
  propertyType: "",
  address: "",
  city: "",
  zip: "",
  bedrooms: "",
  bathrooms: "",
  squareFootage: "",
  occupancy: "",
  pets: "",
  overallCondition: "",
  kitchenCondition: "",
  bathroomCondition: "",
  floorsCondition: "",
  additionalConditions: [],
  checkoutTime: "",
  checkinTime: "",
  turnoversPerMonth: "",
  laundryNeeded: "",
  linenSets: "",
  replacementLinensOnsite: "",
  restockingNeeded: "",
  suppliesProvidedByHost: "",
  completionPhotos: "",
  accessInstructions: "",
  specialInstructions: "",
  preferredDate: "",
  flexibleDate: false,
  frequency: "",
  addons: [],
  message: "",
  consent: false,
  company_website: "",
};

const MAX_FILES = 6;
const MAX_SIZE = 8 * 1024 * 1024;
const NETLIFY_FORM_ENDPOINT = "/forms.html";

function Field({ label, htmlFor, required, children, error, optional }) {
  return (
    <div className="flex flex-col">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink mb-2">
        {label} {required && <span className="text-rose" aria-hidden="true">*</span>}
        {optional && <span className="text-ink/45 font-normal"> (optional)</span>}
      </label>
      {children}
      <span className="min-h-[1.1rem] mt-1 text-xs text-red-700" role="alert">
        {error || ""}
      </span>
    </div>
  );
}

const inputCls =
  "bg-white border border-ink/20 px-4 py-3 text-ink placeholder-ink/40 focus:border-rose outline-none transition-colors w-full";

function GroupTitle({ children }) {
  return (
    <div className="sm:col-span-2 mt-2 mb-1">
      <span className="eyebrow text-rose">{children}</span>
      <span className="mt-2 block h-px w-10 bg-rose" aria-hidden="true" />
    </div>
  );
}

function AssessmentGroup({ title, name, options, value, onChange, error }) {
  return (
    <fieldset className="sm:col-span-2 mb-1" data-invalid={!!error}>
      <legend className="text-sm font-medium text-ink mb-3">
        {title} <span className="text-rose" aria-hidden="true">*</span>
      </legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {options.map((o) => {
          const on = value === o.value;
          return (
            <label
              key={o.value}
              className={`cursor-pointer border p-4 transition-colors ${
                on ? "border-rose bg-blush-soft/40" : "border-ink/15 bg-white hover:border-rose/50"
              }`}
              data-testid={`${name}-${o.value.toLowerCase()}`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name={name}
                  value={o.value}
                  checked={on}
                  onChange={onChange}
                  className="h-4 w-4 accent-rose shrink-0"
                />
                <span className="text-sm font-semibold text-ink">{o.value}</span>
              </span>
              <span className="mt-2 block text-xs text-ink/60 leading-relaxed">{o.desc}</span>
            </label>
          );
        })}
      </div>
      <span className="min-h-[1.1rem] mt-1 block text-xs text-red-700" role="alert">
        {error || ""}
      </span>
    </fieldset>
  );
}

export default function EstimateForm() {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleAddon = (a) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.includes(a) ? f.addons.filter((x) => x !== a) : [...f.addons, a],
    }));

  const toggleAdditional = (c) =>
    setForm((f) => {
      if (c === "None of the above") {
        return {
          ...f,
          additionalConditions: f.additionalConditions.includes(c) ? [] : ["None of the above"],
        };
      }
      const rest = f.additionalConditions.filter((x) => x !== "None of the above");
      return {
        ...f,
        additionalConditions: rest.includes(c) ? rest.filter((x) => x !== c) : [...rest, c],
      };
    });

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const merged = [...files];
    let err = "";
    for (const f of picked) {
      if (!f.type.startsWith("image/")) { err = "Please upload image files only."; continue; }
      if (f.size > MAX_SIZE) { err = "Each photo must be under 8MB."; continue; }
      if (merged.length >= MAX_FILES) { err = `You can add up to ${MAX_FILES} photos.`; break; }
      merged.push(f);
    }
    setFiles(merged);
    setFileError(err);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((fs) => fs.filter((_, idx) => idx !== i));

  const validate = () => {
    const er = {};
    if (!form.firstName.trim()) er.firstName = "Please enter your first name.";
    if (!form.lastName.trim()) er.lastName = "Please enter your last name.";
    if (!/^[0-9()+\-.\s]{7,}$/.test(form.phone)) er.phone = "Please enter a valid phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.service) er.service = "Please select a service.";
    if (!form.bedrooms.trim()) er.bedrooms = "Please enter the number of bedrooms.";
    if (!form.bathrooms.trim()) er.bathrooms = "Please enter the number of bathrooms.";
    if (!form.occupancy) er.occupancy = "Please tell us if the property is occupied or vacant.";
    if (!form.pets) er.pets = "Please let us know if there are pets.";
    if (!form.overallCondition) er.overallCondition = "Please select the overall condition.";
    if (!form.kitchenCondition) er.kitchenCondition = "Please select the kitchen condition.";
    if (!form.bathroomCondition) er.bathroomCondition = "Please select the bathroom condition.";
    if (!form.floorsCondition) er.floorsCondition = "Please select the floors & dust condition.";
    if (form.service === AIRBNB_SERVICE) {
      if (!form.checkoutTime) er.checkoutTime = "Please enter the typical checkout time.";
      if (!form.checkinTime) er.checkinTime = "Please enter the typical check-in time.";
      if (!form.linenSets.trim()) er.linenSets = "Please enter the number of beds / linen sets.";
      if (!form.laundryNeeded) er.laundryNeeded = "Please let us know if laundry service is needed.";
      if (!form.replacementLinensOnsite) er.replacementLinensOnsite = "Please let us know if clean replacement linens are onsite.";
      if (!form.restockingNeeded) er.restockingNeeded = "Please let us know if restocking is needed.";
      if (!form.suppliesProvidedByHost) er.suppliesProvidedByHost = "Please let us know if supplies are provided by the host.";
    }
    if (!form.consent) er.consent = "Please provide consent so we can contact you.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const first = document.querySelector("[data-invalid=true]");
      first && first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStatus("submitting");

    const isPreview = /emergent/i.test(window.location.hostname);
    let netlify;
    if (isPreview) {
      netlify = Promise.resolve(false);
    } else if (files.length) {
      // Netlify file uploads require multipart FormData (browser sets the
      // boundary) and only ONE file per field, so map each photo to photo1..N.
      const fd = new FormData();
      fd.append("form-name", "estimate");
      Object.entries(form).forEach(([k, v]) =>
        fd.append(k, Array.isArray(v) ? v.join(", ") : v ?? "")
      );
      files.forEach((f, i) => fd.append(`photo${i + 1}`, f, f.name));
      netlify = fetch(NETLIFY_FORM_ENDPOINT, { method: "POST", body: fd })
        .then((r) => r.ok)
        .catch(() => false);
    } else {
      // No files: Netlify expects a URL-encoded body for AJAX submissions.
      const params = new URLSearchParams();
      params.append("form-name", "estimate");
      Object.entries(form).forEach(([k, v]) =>
        params.append(k, Array.isArray(v) ? v.join(", ") : v ?? "")
      );
      netlify = fetch(NETLIFY_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      })
        .then((r) => r.ok)
        .catch(() => false);
    }

    const backend = fetch(`${API}/estimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        photoCount: files.length,
        photoNames: files.map((f) => f.name),
      }),
    }).then((r) => r.ok).catch(() => false);

    const [okNetlify, okBackend] = await Promise.all([netlify, backend]);
    setStatus(okNetlify || okBackend ? "success" : "error");
  };

  if (status === "success") {
    return (
      <div className="bg-white border border-gold/40 p-10 text-center" data-testid="estimate-success" role="status">
        <CheckCircle2 size={44} strokeWidth={1.25} className="mx-auto text-rose" aria-hidden="true" />
        <h2 className="mt-5 text-3xl text-ink">Thank You — Request Received</h2>
        <p className="mt-4 text-ink/70 max-w-md mx-auto leading-relaxed">
          We've received your request and will reach out about your free estimate.
          Need a faster response? Call or text us anytime.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
          <a href={TEL_HREF} className="btn-ghost" data-testid="success-call">
            <Phone size={16} strokeWidth={1.75} aria-hidden="true" /> Call {PHONE_DISPLAY}
          </a>
          <a href={SMS_HREF} className="btn-ghost" data-testid="success-text">
            <MessageSquareText size={16} strokeWidth={1.75} aria-hidden="true" /> Text Us
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      name="estimate"
      method="POST"
      data-netlify="true"
      netlify-honeypot="company_website"
      onSubmit={onSubmit}
      noValidate
      className="bg-white border border-gold/25 p-6 sm:p-9"
      data-testid="estimate-form"
    >
      <input type="hidden" name="form-name" value="estimate" />
      <div aria-hidden="true" className="hidden">
        <label htmlFor="company_website">Company Website</label>
        <input id="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set("company_website")} />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6">
        <GroupTitle>Contact Information</GroupTitle>
        <Field label="First Name" htmlFor="firstName" required error={errors.firstName}>
          <input id="firstName" className={inputCls} data-invalid={!!errors.firstName} value={form.firstName} onChange={set("firstName")} autoComplete="given-name" data-testid="field-firstName" />
        </Field>
        <Field label="Last Name" htmlFor="lastName" required error={errors.lastName}>
          <input id="lastName" className={inputCls} data-invalid={!!errors.lastName} value={form.lastName} onChange={set("lastName")} autoComplete="family-name" data-testid="field-lastName" />
        </Field>
        <Field label="Phone" htmlFor="phone" required error={errors.phone}>
          <input id="phone" type="tel" className={inputCls} data-invalid={!!errors.phone} value={form.phone} onChange={set("phone")} autoComplete="tel" data-testid="field-phone" />
        </Field>
        <Field label="Email" htmlFor="email" required error={errors.email}>
          <input id="email" type="email" className={inputCls} data-invalid={!!errors.email} value={form.email} onChange={set("email")} autoComplete="email" data-testid="field-email" />
        </Field>
        <Field label="Preferred Contact Method" htmlFor="preferredContact">
          <select id="preferredContact" className={inputCls} value={form.preferredContact} onChange={set("preferredContact")} data-testid="field-preferredContact">
            <option>Call</option><option>Text</option><option>Email</option>
          </select>
        </Field>
        <Field label="Company / Property Name" htmlFor="companyName" optional>
          <input id="companyName" name="companyName" className={inputCls} placeholder="If applicable" value={form.companyName} onChange={set("companyName")} autoComplete="organization" data-testid="field-companyName" />
        </Field>

        <GroupTitle>Service Needed</GroupTitle>
        <Field label="Service" htmlFor="service" required error={errors.service}>
          <select id="service" className={inputCls} data-invalid={!!errors.service} value={form.service} onChange={set("service")} data-testid="field-service">
            <option value="">Select a service…</option>
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Residential or Commercial" htmlFor="propertyUse">
          <select id="propertyUse" className={inputCls} value={form.propertyUse} onChange={set("propertyUse")} data-testid="field-propertyUse">
            <option>Residential</option><option>Commercial</option>
          </select>
        </Field>

        <GroupTitle>Property Information</GroupTitle>
        <Field label="Property Type" htmlFor="propertyType" optional>
          <input id="propertyType" className={inputCls} placeholder="Home, apartment, office…" value={form.propertyType} onChange={set("propertyType")} data-testid="field-propertyType" />
        </Field>
        <Field label="Service Address" htmlFor="address" optional>
          <input id="address" className={inputCls} value={form.address} onChange={set("address")} autoComplete="street-address" data-testid="field-address" />
        </Field>
        <Field label="City" htmlFor="city" optional>
          <input id="city" className={inputCls} value={form.city} onChange={set("city")} autoComplete="address-level2" data-testid="field-city" />
        </Field>
        <Field label="ZIP Code" htmlFor="zip" optional>
          <input id="zip" className={inputCls} value={form.zip} onChange={set("zip")} autoComplete="postal-code" data-testid="field-zip" />
        </Field>
        <Field label="Bedrooms" htmlFor="bedrooms" required error={errors.bedrooms}>
          <input id="bedrooms" className={inputCls} data-invalid={!!errors.bedrooms} inputMode="numeric" placeholder="e.g. 3" value={form.bedrooms} onChange={set("bedrooms")} data-testid="field-bedrooms" />
        </Field>
        <Field label="Bathrooms" htmlFor="bathrooms" required error={errors.bathrooms}>
          <input id="bathrooms" className={inputCls} data-invalid={!!errors.bathrooms} inputMode="numeric" placeholder="e.g. 2" value={form.bathrooms} onChange={set("bathrooms")} data-testid="field-bathrooms" />
        </Field>
        <Field label="Approximate Square Footage" htmlFor="squareFootage" optional>
          <input id="squareFootage" className={inputCls} placeholder="e.g. 1,500 sq ft" value={form.squareFootage} onChange={set("squareFootage")} data-testid="field-squareFootage" />
        </Field>
        <Field label="Occupied or Vacant" htmlFor="occupancy" required error={errors.occupancy}>
          <select id="occupancy" className={inputCls} data-invalid={!!errors.occupancy} value={form.occupancy} onChange={set("occupancy")} data-testid="field-occupancy">
            <option value="">Select…</option><option>Occupied</option><option>Vacant</option>
          </select>
        </Field>
        <Field label="Pets in the Home" htmlFor="pets" required error={errors.pets}>
          <div className="flex gap-3" data-invalid={!!errors.pets}>
            {["Yes", "No"].map((v) => {
              const on = form.pets === v;
              return (
                <label
                  key={v}
                  className={`flex-1 cursor-pointer border px-4 py-3 text-sm text-center transition-colors ${
                    on ? "border-rose bg-blush-soft/40 text-ink font-semibold" : "border-ink/20 bg-white text-ink/75 hover:border-rose/50"
                  }`}
                  data-testid={`pets-${v.toLowerCase()}`}
                >
                  <input type="radio" name="pets" value={v} checked={on} onChange={set("pets")} className="sr-only" />
                  {v}
                </label>
              );
            })}
          </div>
        </Field>

        <GroupTitle>Current Cleaning Condition</GroupTitle>
        <p className="sm:col-span-2 -mt-1 mb-3 text-sm text-ink/65 leading-relaxed">
          Please select the options that best describe the current condition of the property. This
          helps us provide accurate pricing and allow enough time for your service.
        </p>
        {ASSESSMENTS.map((a) => (
          <AssessmentGroup
            key={a.key}
            title={a.title}
            name={a.key}
            options={a.options}
            value={form[a.key]}
            onChange={set(a.key)}
            error={errors[a.key]}
          />
        ))}
        <fieldset className="sm:col-span-2 mt-1">
          <legend className="text-sm font-medium text-ink mb-3">Additional Conditions <span className="text-ink/45 font-normal">(select all that apply)</span></legend>
          <div className="flex flex-wrap gap-2" data-testid="additional-conditions">
            {ADDITIONAL_CONDITIONS.map((c) => {
              const on = form.additionalConditions.includes(c);
              return (
                <button
                  type="button"
                  key={c}
                  onClick={() => toggleAdditional(c)}
                  aria-pressed={on}
                  className={`px-3.5 py-2 text-sm border transition-colors ${
                    on ? "bg-ink text-white border-ink" : "bg-white text-ink/75 border-ink/20 hover:border-rose"
                  }`}
                  data-testid={`additional-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </fieldset>

        <GroupTitle>Scheduling</GroupTitle>
        <Field label="Preferred Service Date" htmlFor="preferredDate" optional>
          <input id="preferredDate" type="date" className={inputCls} value={form.preferredDate} onChange={set("preferredDate")} data-testid="field-preferredDate" />
        </Field>
        <Field label="Frequency (if recurring)" htmlFor="frequency" optional>
          <select id="frequency" className={inputCls} value={form.frequency} onChange={set("frequency")} data-testid="field-frequency">
            <option value="">One-time</option><option>Weekly</option><option>Biweekly</option><option>Monthly</option><option>Other</option>
          </select>
        </Field>
        <label className="sm:col-span-2 flex items-center gap-3 -mt-2 mb-2 text-sm text-ink/75">
          <input type="checkbox" checked={form.flexibleDate} onChange={set("flexibleDate")} className="h-5 w-5 accent-rose" data-testid="field-flexibleDate" />
          My date is flexible
        </label>

        {form.service === AIRBNB_SERVICE && (
          <>
            <GroupTitle>Short-Term Rental Details</GroupTitle>
            <Field label="Typical Checkout Time" htmlFor="checkoutTime" required error={errors.checkoutTime}>
              <input id="checkoutTime" type="time" className={inputCls} data-invalid={!!errors.checkoutTime} value={form.checkoutTime} onChange={set("checkoutTime")} data-testid="field-checkoutTime" />
            </Field>
            <Field label="Typical Check-In Time" htmlFor="checkinTime" required error={errors.checkinTime}>
              <input id="checkinTime" type="time" className={inputCls} data-invalid={!!errors.checkinTime} value={form.checkinTime} onChange={set("checkinTime")} data-testid="field-checkinTime" />
            </Field>
            <Field label="Average Turnovers per Month" htmlFor="turnoversPerMonth" optional>
              <input id="turnoversPerMonth" className={inputCls} inputMode="numeric" placeholder="e.g. 8" value={form.turnoversPerMonth} onChange={set("turnoversPerMonth")} data-testid="field-turnoversPerMonth" />
            </Field>
            <Field label="Number of Beds / Linen Sets" htmlFor="linenSets" required error={errors.linenSets}>
              <input id="linenSets" className={inputCls} data-invalid={!!errors.linenSets} placeholder="e.g. 3 beds / 3 sets" value={form.linenSets} onChange={set("linenSets")} data-testid="field-linenSets" />
            </Field>
            <Field label="Is Laundry Service Needed?" htmlFor="laundryNeeded" required error={errors.laundryNeeded}>
              <select id="laundryNeeded" className={inputCls} data-invalid={!!errors.laundryNeeded} value={form.laundryNeeded} onChange={set("laundryNeeded")} data-testid="field-laundryNeeded">
                <option value="">Select…</option>{YESNO.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Clean Replacement Linens Onsite?" htmlFor="replacementLinensOnsite" required error={errors.replacementLinensOnsite}>
              <select id="replacementLinensOnsite" className={inputCls} data-invalid={!!errors.replacementLinensOnsite} value={form.replacementLinensOnsite} onChange={set("replacementLinensOnsite")} data-testid="field-replacementLinensOnsite">
                <option value="">Select…</option>{YESNO.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Is Restocking Needed?" htmlFor="restockingNeeded" required error={errors.restockingNeeded}>
              <select id="restockingNeeded" className={inputCls} data-invalid={!!errors.restockingNeeded} value={form.restockingNeeded} onChange={set("restockingNeeded")} data-testid="field-restockingNeeded">
                <option value="">Select…</option>{YESNO.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Cleaning / Restocking Supplies Provided by Host?" htmlFor="suppliesProvidedByHost" required error={errors.suppliesProvidedByHost}>
              <select id="suppliesProvidedByHost" className={inputCls} data-invalid={!!errors.suppliesProvidedByHost} value={form.suppliesProvidedByHost} onChange={set("suppliesProvidedByHost")} data-testid="field-suppliesProvidedByHost">
                <option value="">Select…</option>{YESNO.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Would You Like Completion Photos?" htmlFor="completionPhotos" optional>
              <select id="completionPhotos" className={inputCls} value={form.completionPhotos} onChange={set("completionPhotos")} data-testid="field-completionPhotos">
                <option value="">Select…</option>{YESNO.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Access Instructions / Method" htmlFor="accessInstructions" optional>
              <input id="accessInstructions" className={inputCls} placeholder="e.g. lockbox code, smart lock, key pickup" value={form.accessInstructions} onChange={set("accessInstructions")} data-testid="field-accessInstructions" />
            </Field>
            <div className="sm:col-span-2 flex flex-col">
              <label htmlFor="specialInstructions" className="text-sm font-medium text-ink mb-2">
                Special Cleaning Instructions <span className="text-ink/45 font-normal">(optional)</span>
              </label>
              <textarea id="specialInstructions" rows={3} className={inputCls} placeholder="Anything specific to this property or guests" value={form.specialInstructions} onChange={set("specialInstructions")} data-testid="field-specialInstructions" />
            </div>
          </>
        )}
      </div>

      {/* Add-ons */}
      <fieldset className="mt-4 mb-2">
        <legend className="eyebrow text-rose">Customize Your Clean — Add-Ons</legend>
        <span className="mt-2 mb-4 block h-px w-10 bg-rose" aria-hidden="true" />
        <div className="flex flex-wrap gap-2" data-testid="addons">
          {CLEANING_ADDONS.map((a) => {
            const on = form.addons.includes(a);
            return (
              <button
                type="button"
                key={a}
                onClick={() => toggleAddon(a)}
                aria-pressed={on}
                className={`px-3.5 py-2 text-sm border transition-colors ${on ? "bg-ink text-white border-ink" : "bg-white text-ink/75 border-ink/20 hover:border-rose"}`}
                data-testid={`addon-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {a}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-4">
        <label htmlFor="message" className="text-sm font-medium text-ink mb-2 block">Tell Us About Your Project</label>
        <textarea id="message" rows={5} className={inputCls} placeholder="Tell us about your property and what you'd like help with." value={form.message} onChange={set("message")} data-testid="field-message" />
      </div>

      {/* Photo upload */}
      <div className="mt-4 mb-4">
        <span className="text-sm font-medium text-ink mb-2 block">
          Add Photos <span className="text-ink/50 font-normal">(optional — great for junk removal, deep cleans & move-outs)</span>
        </span>
        <label htmlFor="photos" className="flex items-center gap-3 cursor-pointer border border-dashed border-rose/40 bg-blush-soft/40 px-4 py-4 hover:border-rose transition-colors">
          <ImagePlus size={20} className="text-rose shrink-0" aria-hidden="true" />
          <span className="text-sm text-ink/70">Click to upload images — JPG or PNG, up to 10MB each</span>
          <input id="photos" name="photos" type="file" accept="image/*" multiple className="sr-only" onChange={onFiles} data-testid="field-photos" />
        </label>
        <span className="min-h-[1.1rem] block mt-1 text-xs text-red-700" role="alert">{fileError || ""}</span>
        {files.length > 0 && (
          <ul className="mt-1 flex flex-wrap gap-2" data-testid="photo-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 bg-white border border-gold/25 px-3 py-1.5 text-xs text-ink/75">
                <span className="max-w-[10rem] truncate">{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${f.name}`} className="text-ink/50 hover:text-rose"><X size={14} aria-hidden="true" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-start gap-3 mt-1">
        <input id="consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-5 w-5 accent-rose shrink-0" data-invalid={!!errors.consent} data-testid="field-consent" />
        <label htmlFor="consent" className="text-sm text-ink/75 leading-relaxed">
          I agree to be contacted by Authentically Yours Property Services about my request. <span className="text-rose" aria-hidden="true">*</span>
        </label>
      </div>
      <span className="min-h-[1.1rem] block mt-1 text-xs text-red-700" role="alert">{errors.consent || ""}</span>

      {status === "error" && (
        <p className="mt-2 text-sm text-red-700" role="alert">
          Something went wrong. Please try again or call/text {PHONE_DISPLAY}.
        </p>
      )}

      <button type="submit" disabled={status === "submitting"} className="btn-primary w-full mt-4 disabled:opacity-70" data-testid="estimate-submit">
        {status === "submitting" ? (
          <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Sending…</>
        ) : (
          "Request My Free Estimate"
        )}
      </button>
    </form>
  );
}
